import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input } from '@angular/core';
import { TsButtonModule, TsPageHeaderModule, TsSnackbarService } from 'tsui';
import { IncomeStatementEntry, ReportPeriod } from '../../models';
import { FinancialStatementGridComponent } from '../../components/financial-statement-grid/financial-statement-grid.component';
import { BehaviorSubject, map, noop, Observable, Subject } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { Store } from '@ngxs/store';
import { AssetManagementState } from '../../state/asset-management.state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssetManagementLoaderService } from '../../services/asset-management-loader.service';
import { AssetManagementRoutingService } from '../../services/asset-management-routing.service';
import { combineLatestWith } from 'rxjs/operators';

@Component({
  selector: 'income-statement-container',
  standalone: true,
  imports: [TsButtonModule, TsPageHeaderModule, FinancialStatementGridComponent, AsyncPipe],
  template: `
    <financial-statement-grid
      mode="income-statement"
      [loading]="incomeStatementLoading | async"
      [reportPeriod]="sharedReportPeriodFilter$ | async"
      [financialStatement]="incomeStatement$ | async"
      (reportPeriodChanged)="reportPeriodTrigger.next($event)"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncomeStatementContainerComponent {
  readonly store = inject(Store);
  readonly destroyRef = inject(DestroyRef);
  readonly snackbar = inject(TsSnackbarService);
  readonly assetManagementRoutingService = inject(AssetManagementRoutingService);

  readonly incomeStatementLoading = new BehaviorSubject<boolean>(false);
  readonly sharedReportPeriodFilter$ = this.store.select(AssetManagementState.sharedReportPeriodFilter);
  readonly reportPeriodTrigger = new BehaviorSubject<ReportPeriod>(
    this.store.selectSnapshot(AssetManagementState.sharedReportPeriodFilter),
  );
  readonly incomeStatement$: Observable<IncomeStatementEntry[]> = this.store.select(
    AssetManagementState.incomeStatement,
  );
  readonly loader = inject(AssetManagementLoaderService);
  readonly queryParamsLoaded = new Subject<void>();

  get propertyId() {
    return this.store.selectSnapshot(AssetManagementState.propertyId);
  }

  ngOnInit() {
    this.reportPeriodTrigger
      .pipe(
        combineLatestWith(this.queryParamsLoaded),
        map(([reportPeriod, _]) => reportPeriod),
        this.loader.loadFinancialStatement('income-statement', this.propertyId, this.incomeStatementLoading),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(noop);

    this.assetManagementRoutingService.retrieveQueryParamsFilters(this.reportPeriodTrigger, this.queryParamsLoaded);
  }
}
